import { CloudUploadOutlined, MobileOutlined } from '@ant-design/icons'
import type { MouseEvent } from 'react'
import './asset-upload-action-tile.css'

export const UPLOAD_ACTION_TILE_KEY = '__upload_actions__'

export function isolateUploadActionEvent(event: {
  preventDefault: () => void
  stopPropagation: () => void
}) {
  event.preventDefault()
  event.stopPropagation()
}

export type AssetUploadActionTileLayout = 'grid' | 'list'

export interface AssetUploadActionTileProps {
  layout: AssetUploadActionTileLayout
  mobileLabel: string
  localLabel: string
  tileLabel: string
  onMobileClick: () => void
  onLocalClick: () => void
  mobileDisabled?: boolean
  localDisabled?: boolean
  testIdPrefix: string
  markerPrefix: 'iap' | 'vap' | 'aap'
  gridMinHeight?: number
}

export default function AssetUploadActionTile({
  layout,
  mobileLabel,
  localLabel,
  tileLabel,
  onMobileClick,
  onLocalClick,
  mobileDisabled = false,
  localDisabled = false,
  testIdPrefix,
  markerPrefix,
  gridMinHeight,
}: AssetUploadActionTileProps) {
  const wrapHandler = (handler: () => void) => (e: MouseEvent) => {
    isolateUploadActionEvent(e)
    handler()
  }

  if (layout === 'list') {
    return (
      <div
        className="asset-upload-list-row"
        data-testid={`${testIdPrefix}-upload-list-actions`}
        role="group"
        aria-label={tileLabel}
      >
        <button
          type="button"
          className="asset-upload-list-half"
          data-testid={`${testIdPrefix}-mobile-upload`}
          disabled={mobileDisabled}
          aria-label={mobileLabel}
          onPointerDown={isolateUploadActionEvent}
          onMouseDown={isolateUploadActionEvent}
          onClick={wrapHandler(onMobileClick)}
        >
          <MobileOutlined className="asset-upload-half-icon" aria-hidden="true" />
          <span className="asset-upload-half-label">{mobileLabel}</span>
        </button>
        <div className="asset-upload-list-divider" aria-hidden="true" />
        <button
          type="button"
          className="asset-upload-list-half"
          data-testid={`${testIdPrefix}-local-upload`}
          disabled={localDisabled}
          aria-label={localLabel}
          onPointerDown={isolateUploadActionEvent}
          onMouseDown={isolateUploadActionEvent}
          onClick={wrapHandler(onLocalClick)}
        >
          <CloudUploadOutlined className="asset-upload-half-icon" aria-hidden="true" />
          <span className="asset-upload-half-label">{localLabel}</span>
        </button>
      </div>
    )
  }

  return (
    <div
      key={UPLOAD_ACTION_TILE_KEY}
      className="asset-upload-tile-split"
      style={gridMinHeight ? { minHeight: gridMinHeight } : undefined}
      data-iap-upload-tile={markerPrefix === 'iap' ? 'true' : undefined}
      data-vap-upload-tile={markerPrefix === 'vap' ? 'true' : undefined}
      data-iap-upload-action-tile={markerPrefix === 'iap' ? 'true' : undefined}
      data-vap-upload-action-tile={markerPrefix === 'vap' ? 'true' : undefined}
      data-testid={`${testIdPrefix}-upload-tile`}
      role="group"
      aria-label={tileLabel}
    >
      <button
        type="button"
        className="asset-upload-half asset-upload-half--mobile"
        data-testid={`${testIdPrefix}-mobile-upload`}
        disabled={mobileDisabled}
        aria-label={mobileLabel}
        onPointerDown={isolateUploadActionEvent}
        onMouseDown={isolateUploadActionEvent}
        onClick={wrapHandler(onMobileClick)}
      >
        <MobileOutlined className="asset-upload-half-icon" aria-hidden="true" />
        <span className="asset-upload-half-label">{mobileLabel}</span>
      </button>
      <div className="asset-upload-divider" aria-hidden="true" />
      <button
        type="button"
        className="asset-upload-half asset-upload-half--local"
        data-testid={`${testIdPrefix}-local-upload`}
        disabled={localDisabled}
        aria-label={localLabel}
        onPointerDown={isolateUploadActionEvent}
        onMouseDown={isolateUploadActionEvent}
        onClick={wrapHandler(onLocalClick)}
      >
        <CloudUploadOutlined className="asset-upload-half-icon" aria-hidden="true" />
        <span className="asset-upload-half-label">{localLabel}</span>
      </button>
    </div>
  )
}
